// Browser entry: wire the live SDK to the Console and mount React.
import { StrictMode, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { PaletteMode } from "@mui/material";
import { createRoot } from "react-dom/client";
import CssBaseline from "@mui/material/CssBaseline";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";

// Self-hosted fonts bundled by Vite so the UI renders consistently without a network font request.
// no runtime Google Fonts dependency. Only the weights the theme uses.
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/500.css";
import "@fontsource/ibm-plex-sans/600.css";
import "@fontsource/ibm-plex-sans/700.css";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";

import { App } from "./App";
import { makeAuthedFetch, makeClient } from "./client";
import { createAtlasTheme } from "./theme";
// Bundled by Vite (base "/dashboard/") -> emitted as /dashboard/console.css. Replaces the old
// static <link> in index.html.
import "./index.css";

// The platform SDK (window.atlas.*) lives at the fixed absolute path /navigation/atlas-navbar.js.
// It is injected here at runtime rather than referenced in index.html because Vite rebases
// root-relative HTML URLs by `base` (which would break it to /dashboard/navigation/...). A literal
// string in JS is never rewritten. Skip injection if something already set up the SDK (e.g. a
// dev-only stub loaded before this module).
const SDK_SRC = "/navigation/atlas-navbar.js";

// Load the SDK, then start the app.
//
// Dev-only: `vite dev` with VITE_ATLAS_STUB=1 dynamically imports a local fake SDK
// (console/dev/atlas-stub.js, which sets window.atlas synchronously) so the authed views work
// locally without a Cognito callback (there's no localhost callback). The dynamic import goes
// through Vite's module graph (a plain <script src> wouldn't be served under `base`). Every other
// run (prod build, or plain `vite dev`) injects the real SDK <script> at its literal absolute path.
// import.meta.env.DEV is statically false in a build, so the dev-only branch is dropped and never ships.
async function loadSdk() {
  if (window.atlas) return; // already available (a prior load)
  if (import.meta.env.DEV && import.meta.env.VITE_ATLAS_STUB === "1") {
    await import("../dev/atlas-stub.js");
    return;
  }
  if (document.querySelector(`script[src="${SDK_SRC}"]`)) return;
  const s = document.createElement("script");
  s.src = SDK_SRC;
  s.defer = true;
  document.head.appendChild(s);
}

// The SDK loads asynchronously, so it may not be on `window` the instant this runs. Poll briefly,
// but give up after a bound and show a real message instead of a silent blank page (e.g. if the
// navigation SDK app isn't deployed, atlas-navbar.js 404s and never sets window.atlas).
const SDK_TIMEOUT_MS = 5000;
const POLL_MS = 50;

function mount(view: ReactNode) {
  const root = document.getElementById("root");
  if (!root) throw new Error("missing #root element");
  createRoot(root).render(<StrictMode>{view}</StrictMode>);
}

function initialColorMode(): PaletteMode {
  const saved = window.localStorage.getItem("atlas.console.colorMode");
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function ConsoleRoot({ client }: { client: ReturnType<typeof makeClient> }) {
  const [mode, setMode] = useState<PaletteMode>(initialColorMode);
  const theme = useMemo(() => createAtlasTheme(mode), [mode]);

  useEffect(() => {
    window.localStorage.setItem("atlas.console.colorMode", mode);
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App
        client={client}
        colorMode={mode}
        onToggleColorMode={() => setMode((current) => (current === "light" ? "dark" : "light"))}
      />
    </ThemeProvider>
  );
}

function ErrorRoot() {
  const mode = initialColorMode();
  return (
    <ThemeProvider theme={createAtlasTheme(mode)}>
      <CssBaseline />
      {sdkError()}
    </ThemeProvider>
  );
}

function sdkError() {
  return (
    <Stack sx={{ minHeight: "100dvh", px: 2, py: 6 }} alignItems="center" justifyContent="center">
      <Paper elevation={1} sx={{ width: "100%", maxWidth: 520, p: { xs: 3, sm: 4 } }}>
        <Stack spacing={2}>
          <Typography variant="h4" component="h1">
            Atlas Console
          </Typography>
          <Typography color="error.main" fontWeight={600}>
            Could not load the Atlas platform SDK.
          </Typography>
          <Typography color="text.secondary">
            The navigation service may be unavailable or blocked by your network. Reload the page
            to try again.
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
}

function start(elapsed = 0) {
  const atlas = window.atlas;
  if (atlas) {
    const client = makeClient(atlas, makeAuthedFetch(atlas));
    mount(<ConsoleRoot client={client} />);
    return;
  }
  if (elapsed >= SDK_TIMEOUT_MS) {
    mount(<ErrorRoot />);
    return;
  }
  setTimeout(() => start(elapsed + POLL_MS), POLL_MS);
}

loadSdk().finally(() => start());
